import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import ReturnsFields from '../ReturnsFields';
import AlertInlineMessage from '@components/ui/AlertInlineMessage';
import Button from '@components/ui/Button';
import LoadingSpinner from '@components/ui/LoadingSpinner';

import styles from './ReturnsForm.module.scss';
import useReturnsForm from '@hooks/useReturnsForm';
import { useTranslation } from 'next-i18next';

const ReturnsForm = (props: {
  onSuccess?: (orderId: string) => void;
  onError?: (message: string) => void;
}) => {
  const { onSuccess, onError } = props;
  const {
    email,
    orderNumber,
    postalCode,
    errorMessage,
    handleSubmit,
    isLoading,
    isDisabled,
    isDirty,
  } = useReturnsForm({ onSuccess, onError });
  const { t } = useTranslation('returns');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [disableButton, setDisableButton] = useState(false);

  useEffect(() => {
    setDisableButton(false);
  }, [email.value, orderNumber.value, postalCode.value]);

  const onSubmitWrapper = async (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();
    await handleSubmit(recaptchaRef?.current, e);
  };

  return (
    <>
      {errorMessage && (
        <AlertInlineMessage
          classNames={styles.loginErrorMessage}
          messageType="ERROR"
        >
          <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
        </AlertInlineMessage>
      )}

      <form onSubmit={onSubmitWrapper} className={styles.formContainer}>
        <ReturnsFields
          email={email}
          orderNumber={orderNumber}
          postalCode={postalCode}
          isDirty={isDirty}
        />
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          badge="bottomleft"
          sitekey={`${process.env.NEXT_PUBLIC_RECAPTCHA_KEY}`}
        />
        <Button
          variant="primary"
          type="submit"
          disabled={isDisabled || isLoading || disableButton}
          label={
            isLoading ? (
              <LoadingSpinner
                classNames={styles.loadingSpinner}
                colour="var(--color-white)"
              />
            ) : (
              <>{t('returns.lookup.button')}</>
            )
          }
          size="large"
          className={cn(styles.loginButton, {
            [styles.isDisabled]: isLoading,
          })}
          dataTestid="login-button"
        />
      </form>
    </>
  );
};

export default ReturnsForm;
